/**
 * i18next bootstrap (TMMAPP-0001). Catalogs are bundled at build time — CSP-clean, no CDN, no
 * runtime fetch. One namespace (`translation`). Initial language: the stored preference, else the
 * browser language, else `en`. Every language change persists the preference and sets
 * `<html lang>`.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, languageTag, normalizeLocale, SUPPORTED_LOCALES } from './constants';
import { readStoredLocale, writeStoredLocale } from './localePreference';
import { RESOURCES } from '@/i18n/resources';

export function resolveInitialLocale(): string {
  return (
    readStoredLocale() ??
    normalizeLocale(typeof navigator !== 'undefined' ? navigator.language : null) ??
    DEFAULT_LOCALE
  );
}

function applyLanguage(lng: string) {
  const normalized = normalizeLocale(lng) ?? DEFAULT_LOCALE;
  writeStoredLocale(normalized);
  document.documentElement.lang = languageTag(normalized);
}

/**
 * TMM-L1 / L4 (TMMAPP-0080, TMMAPP-0082): i18next's built-in formatters hand Intl the bare language
 * (`pt`). Every one of them is re-registered here with the BCP-47 tag (`pt` → `pt-BR`) mapped
 * explicitly, so none relies on ICU's default for bare `pt`. The bodies are i18next's own; only the
 * tag differs. Add a formatter to the app and it belongs in this list, with a test.
 */
function mapFormattersToBrazilianPortuguese() {
  const formatter = i18next.services.formatter;
  if (!formatter) return;
  const tagOf = (lng: string | undefined) => {
    const normalized = normalizeLocale(lng);
    return normalized ? languageTag(normalized) : lng;
  };
  formatter.addCached('number', (lng, options) => {
    const intl = new Intl.NumberFormat(tagOf(lng), { ...options });
    return (value) => intl.format(value);
  });
  formatter.addCached('currency', (lng, options) => {
    const intl = new Intl.NumberFormat(tagOf(lng), { ...options, style: 'currency' });
    return (value) => intl.format(value);
  });
  formatter.addCached('datetime', (lng, options) => {
    const intl = new Intl.DateTimeFormat(tagOf(lng), { ...options });
    return (value) => intl.format(value);
  });
  formatter.addCached('relativetime', (lng, options) => {
    const intl = new Intl.RelativeTimeFormat(tagOf(lng), { ...options });
    return (value) =>
      intl.format(value, (options as { range?: Intl.RelativeTimeFormatUnit }).range || 'day');
  });
  formatter.addCached('list', (lng, options) => {
    const intl = new Intl.ListFormat(tagOf(lng), { ...options });
    return (value) => intl.format(value);
  });
}

if (!i18next.isInitialized) {
  const initial = resolveInitialLocale();
  document.documentElement.lang = languageTag(normalizeLocale(initial) ?? DEFAULT_LOCALE);
  i18next.on('languageChanged', applyLanguage);
  void i18next.use(initReactI18next).init({
    resources: RESOURCES,
    lng: initial,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
  mapFormattersToBrazilianPortuguese();
}

export { i18next };
export default i18next;
